import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Download, Edit3, Save, Users, Wrench } from 'lucide-react';
import { hrApi } from '../../../api';
import { useMasterData } from '../../../../utils/useMasterData';

const STATUS_STYLES = {
  PRESENT: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  ABSENT: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  LATE: { bg: '#ffedd5', color: '#9a3412', border: '#fdba74' },
  HALF_DAY: { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  HOLIDAY: { bg: '#e2e8f0', color: '#334155', border: '#cbd5e1' },
};

const fmtDate = (d) => new Date(d).toISOString().slice(0, 10);
const fmtMonth = (d) => new Date(d).toISOString().slice(0, 7);
const toHHMM = (iso) => (iso ? new Date(iso).toISOString().slice(11, 16) : '');

const badgeStyle = (status) => {
  const s = STATUS_STYLES[String(status || '').toUpperCase()] || STATUS_STYLES.PRESENT;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 700,
    border: `1px solid ${s.border}`,
    background: s.bg,
    color: s.color,
  };
};

const SkeletonRow = ({ cols = 7 }) => (
  <tr>
    {Array.from({ length: cols }).map((_, idx) => (
      <td key={idx} className="px-3 py-3">
        <div className="h-3 animate-pulse rounded bg-slate-200" />
      </td>
    ))}
  </tr>
);

const buildCalendarDays = (monthStr) => {
  const [y, m] = monthStr.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const startPad = first.getDay();
  const days = [];

  for (let i = 0; i < startPad; i += 1) days.push(null);
  for (let d = 1; d <= last.getDate(); d += 1) {
    days.push(`${monthStr}-${String(d).padStart(2, '0')}`);
  }
  return days;
};

function AttendanceScreen({ addToast, userRole, canWrite: canWriteProp }) {
  const { getValues } = useMasterData('ATTENDANCE_STATUS');
  const statusOptions = getValues('ATTENDANCE_STATUS');
  const workerStatusOptions = statusOptions.filter((status) => status !== 'LATE');
  // ── Top-level: Worker vs Staff toggle ──
  const [entityType, setEntityType] = useState('staff');
  const [tab, setTab] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(fmtDate(new Date()));
  const [month, setMonth] = useState(fmtMonth(new Date()));

  // ── Staff (Employee) state ──
  const [dailyRows, setDailyRows] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ status: 'PRESENT', check_in_time: '', check_out_time: '' });
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [calendarStats, setCalendarStats] = useState({});

  // ── Worker state ──
  const [workerDailyRows, setWorkerDailyRows] = useState([]);
  const [workerDailyLoading, setWorkerDailyLoading] = useState(false);
  const [workerEditingId, setWorkerEditingId] = useState(null);
  const [workerEditForm, setWorkerEditForm] = useState({ status: 'PRESENT' });
  const [workerMonthlyRows, setWorkerMonthlyRows] = useState([]);
  const [workerSummaryLoading, setWorkerSummaryLoading] = useState(false);
  const [workerCalendarStats, setWorkerCalendarStats] = useState({});

  // ═══════════════════════════════════════════
  // STAFF (EMPLOYEE) FETCH FUNCTIONS
  // ═══════════════════════════════════════════
  const fetchDaily = useCallback(async (dateValue) => {
    setDailyLoading(true);
    try {
      const res = await hrApi.get('/hr/attendance', {
        params: { date: dateValue, month, page: 1, per_page: 500 },
      });
      setDailyRows(res.data?.data || []);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Failed to load staff attendance', 'error');
      setDailyRows([]);
    } finally {
      setDailyLoading(false);
    }
  }, [addToast, month]);

  const fetchCalendarMonthlyAttendance = async (monthValue) => {
    try {
      const res = await hrApi.get('/hr/attendance', {
        params: { month: monthValue, page: 1, per_page: 5000 },
      });
      const rows = res.data?.data || [];
      const stats = {};
      rows.forEach((row) => {
        const d = row.date;
        if (!stats[d]) stats[d] = { PRESENT: 0, ABSENT: 0, LATE: 0, HALF_DAY: 0, HOLIDAY: 0 };
        const key = String(row.status || 'PRESENT').toUpperCase();
        if (stats[d][key] !== undefined) stats[d][key] += 1;
      });
      setCalendarStats(stats);
    } catch {
      setCalendarStats({});
    }
  };

  const fetchMonthlySummary = useCallback(async (monthValue) => {
    setSummaryLoading(true);
    try {
      const res = await hrApi.get('/hr/attendance/monthly-summary', {
        params: { month: monthValue },
      });
      const rows = (res.data?.data || []).slice().sort((a, b) => Number(a.attendance_pct || 0) - Number(b.attendance_pct || 0));
      setMonthlyRows(rows);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Failed to load monthly summary', 'error');
      setMonthlyRows([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [addToast]);

  // ═══════════════════════════════════════════
  // WORKER FETCH FUNCTIONS
  // ═══════════════════════════════════════════
  const fetchWorkerDaily = useCallback(async (dateValue) => {
    setWorkerDailyLoading(true);
    try {
      const res = await hrApi.get('/hr/attendance/workers', {
        params: { date: dateValue, month, page: 1, per_page: 500 },
      });
      setWorkerDailyRows(res.data?.data || []);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Failed to load worker attendance', 'error');
      setWorkerDailyRows([]);
    } finally {
      setWorkerDailyLoading(false);
    }
  }, [addToast, month]);

  const fetchWorkerCalendarStats = async (monthValue) => {
    try {
      const res = await hrApi.get('/hr/attendance/workers', {
        params: { month: monthValue, page: 1, per_page: 5000 },
      });
      const rows = res.data?.data || [];
      const stats = {};
      rows.forEach((row) => {
        const d = row.date;
        if (!stats[d]) stats[d] = { PRESENT: 0, ABSENT: 0, HALF_DAY: 0, HOLIDAY: 0 };
        const key = String(row.status || 'PRESENT').toUpperCase();
        if (stats[d][key] !== undefined) stats[d][key] += 1;
      });
      setWorkerCalendarStats(stats);
    } catch {
      setWorkerCalendarStats({});
    }
  };

  const fetchWorkerMonthlySummary = useCallback(async (monthValue) => {
    setWorkerSummaryLoading(true);
    try {
      const res = await hrApi.get('/hr/attendance/workers/monthly-summary', {
        params: { month: monthValue },
      });
      const rows = (res.data?.data || []).slice().sort((a, b) => Number(a.attendance_pct || 0) - Number(b.attendance_pct || 0));
      setWorkerMonthlyRows(rows);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Failed to load worker monthly summary', 'error');
      setWorkerMonthlyRows([]);
    } finally {
      setWorkerSummaryLoading(false);
    }
  }, [addToast]);

  // ═══════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════

  // Clear stale data when switching entity type
  useEffect(() => {
    setEditingId(null);
    setWorkerEditingId(null);
    if (entityType === 'staff') {
      setWorkerDailyRows([]);
      setWorkerMonthlyRows([]);
      setWorkerCalendarStats({});
    } else {
      setDailyRows([]);
      setMonthlyRows([]);
      setCalendarStats({});
    }
  }, [entityType]);

  useEffect(() => {
    if (entityType === 'staff') {
      fetchDaily(selectedDate);
    } else {
      fetchWorkerDaily(selectedDate);
    }
  }, [entityType, fetchDaily, fetchWorkerDaily, selectedDate]);

  useEffect(() => {
    if (entityType === 'staff') {
      fetchCalendarMonthlyAttendance(month);
      if (tab === 'summary') fetchMonthlySummary(month);
    } else {
      fetchWorkerCalendarStats(month);
      if (tab === 'summary') fetchWorkerMonthlySummary(month);
    }
  }, [entityType, fetchMonthlySummary, fetchWorkerMonthlySummary, month, tab]);

  // ═══════════════════════════════════════════
  // STAFF ACTIONS
  // ═══════════════════════════════════════════
  const markAllPresent = async () => {
    const employeeIds = dailyRows.map((row) => row.employee_id).filter(Boolean);
    if (!employeeIds.length) {
      addToast?.('No active staff found for selected date', 'error');
      return;
    }
    try {
      await hrApi.post('/hr/attendance/bulk', {
        date: selectedDate,
        employee_ids: employeeIds,
        status: 'PRESENT',
      });
      addToast?.('All staff marked as PRESENT', 'success');
      fetchDaily(selectedDate);
      fetchCalendarMonthlyAttendance(month);
      if (tab === 'summary') fetchMonthlySummary(month);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Bulk attendance update failed', 'error');
    }
  };

  const openEdit = (row) => {
    setEditingId(row.employee_id);
    setEditForm({
      status: String(row.status || 'PRESENT').toUpperCase(),
      check_in_time: toHHMM(row.check_in_time),
      check_out_time: toHHMM(row.check_out_time),
    });
  };

  const saveEdit = async (employeeId) => {
    try {
      await hrApi.post('/hr/attendance', {
        employee_id: employeeId,
        date: selectedDate,
        status: editForm.status,
        check_in_time: editForm.check_in_time || null,
        check_out_time: editForm.check_out_time || null,
      });
      addToast?.('Attendance updated', 'success');
      setEditingId(null);
      fetchDaily(selectedDate);
      fetchCalendarMonthlyAttendance(month);
      if (tab === 'summary') fetchMonthlySummary(month);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Failed to update attendance', 'error');
    }
  };

  // ═══════════════════════════════════════════
  // WORKER ACTIONS
  // ═══════════════════════════════════════════
  const markAllWorkersPresent = async () => {
    const workerIds = workerDailyRows.map((row) => row.worker_id).filter(Boolean);
    if (!workerIds.length) {
      addToast?.('No active workers found for selected date', 'error');
      return;
    }
    try {
      await hrApi.post('/hr/attendance/workers/bulk', {
        date: selectedDate,
        worker_ids: workerIds,
        status: 'PRESENT',
      });
      addToast?.('All workers marked as PRESENT', 'success');
      fetchWorkerDaily(selectedDate);
      fetchWorkerCalendarStats(month);
      if (tab === 'summary') fetchWorkerMonthlySummary(month);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Worker bulk attendance failed', 'error');
    }
  };

  const openWorkerEdit = (row) => {
    setWorkerEditingId(row.worker_id);
    setWorkerEditForm({ status: String(row.status || 'PRESENT').toUpperCase() });
  };

  const saveWorkerEdit = async (workerId) => {
    try {
      await hrApi.post('/hr/attendance/workers', {
        worker_id: workerId,
        date: selectedDate,
        status: workerEditForm.status,
      });
      addToast?.('Worker attendance updated', 'success');
      setWorkerEditingId(null);
      fetchWorkerDaily(selectedDate);
      fetchWorkerCalendarStats(month);
      if (tab === 'summary') fetchWorkerMonthlySummary(month);
    } catch (err) {
      addToast?.(err.response?.data?.message || 'Failed to update worker attendance', 'error');
    }
  };

  // ═══════════════════════════════════════════
  // EXPORT
  // ═══════════════════════════════════════════
  const exportSummaryCsv = () => {
    const sourceRows = entityType === 'staff' ? monthlyRows : workerMonthlyRows;
    if (!sourceRows.length) {
      addToast?.('No summary rows to export', 'error');
      return;
    }

    const isStaff = entityType === 'staff';
    const header = isStaff
      ? ['Employee', 'Department', 'Present', 'Absent', 'Late', 'Half Day', 'Attendance %']
      : ['Worker', 'Department', 'Daily Wage', 'Present', 'Absent', 'Half Day', 'Attendance %'];
    const rows = [
      header,
      ...sourceRows.map((r) =>
        isStaff
          ? [r.name || '', r.department || '', Number(r.present || 0), Number(r.absent || 0), Number(r.late || 0), Number(r.half_day || 0), Number(r.attendance_pct || 0)]
          : [r.name || '', r.department || '', Number(r.daily_wage || 0), Number(r.present || 0), Number(r.absent || 0), Number(r.half_day || 0), Number(r.attendance_pct || 0)]
      ),
    ];

    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `attendance_${entityType}_${month}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const calendarDays = useMemo(() => buildCalendarDays(month), [month]);
  const activeCalendarStats = entityType === 'staff' ? calendarStats : workerCalendarStats;

  return (
    <div className="space-y-6">
      {/* ── Header Card ── */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Attendance Management</h2>
            <p className="mt-1 text-sm text-slate-600">
              Track daily attendance for {entityType === 'staff' ? 'salaried staff' : 'daily-wage workers'}.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">Month</label>
            <input
              type="month"
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                if (!selectedDate.startsWith(e.target.value)) {
                  setSelectedDate(`${e.target.value}-01`);
                }
              }}
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* ── Entity Type Toggle: Workers / Staff ── */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setEntityType('staff')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
                entityType === 'staff' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Users size={15} /> Staff
            </button>
            <button
              type="button"
              onClick={() => setEntityType('worker')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
                entityType === 'worker' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Wrench size={15} /> Workers
            </button>
          </div>

          <div style={{ width: 1, height: 28, background: '#e2e8f0' }} />

          <div className="inline-flex rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setTab('daily')}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${tab === 'daily' ? 'bg-white text-slate-900' : 'text-slate-600'}`}
            >
              Daily View
            </button>
            <button
              type="button"
              onClick={() => {
                setTab('summary');
                if (entityType === 'staff') fetchMonthlySummary(month);
                else fetchWorkerMonthlySummary(month);
              }}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${tab === 'summary' ? 'bg-white text-slate-900' : 'text-slate-600'}`}
            >
              Monthly Summary
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* DAILY VIEW                                 */}
      {/* ═══════════════════════════════════════════ */}
      {tab === 'daily' ? (
        <>
          {/* Calendar */}
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <CalendarDays size={16} /> Calendar View ({month})
            </div>
            <div className="grid grid-cols-7 gap-2 text-xs font-semibold uppercase text-slate-500">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="px-2 py-1">{d}</div>
              ))}
            </div>
            <div className="mt-2 grid grid-cols-7 gap-2">
              {calendarDays.map((d, idx) => {
                if (!d) return <div key={`empty-${idx}`} className="h-20 rounded-xl border border-transparent" />;
                const stat = activeCalendarStats[d] || {};
                const isActive = d === selectedDate;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setSelectedDate(d)}
                    className={`h-20 rounded-xl border p-2 text-left ${isActive ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white'}`}
                  >
                    <div className="text-sm font-bold text-slate-900">{d.slice(-2)}</div>
                    <div className="mt-1 text-[11px] text-emerald-700">P:{stat.PRESENT || 0}</div>
                    {entityType === 'staff' && <div className="text-[11px] text-orange-700">L:{stat.LATE || 0}</div>}
                    <div className="text-[11px] text-rose-700">A:{stat.ABSENT || 0}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Daily Table */}
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {entityType === 'staff' ? 'Staff' : 'Worker'} Attendance for {selectedDate}
                </h3>
                <p className="text-sm text-slate-500">Click Edit to update status{entityType === 'staff' ? ' and timings' : ''}.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-sm font-semibold text-slate-700">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={entityType === 'staff' ? markAllPresent : markAllWorkersPresent}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  Mark All Present
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              {entityType === 'staff' ? (
                /* ── STAFF TABLE ── */
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-3">Employee Name</th>
                      <th className="px-3 py-3">Dept</th>
                      <th className="px-3 py-3">Check In</th>
                      <th className="px-3 py-3">Check Out</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">OT Hours</th>
                      <th className="px-3 py-3">Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyLoading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cols={7} />)}
                    {!dailyLoading && dailyRows.map((row) => {
                      const isEditing = editingId === row.employee_id;
                      return (
                        <tr key={row.employee_id} className="border-t border-slate-100">
                          <td className="px-3 py-3 font-medium text-slate-900">{row.employee_name}</td>
                          <td className="px-3 py-3 text-slate-700">{row.department || '-'}</td>
                          <td className="px-3 py-3">
                            {isEditing ? (
                              <input
                                type="time"
                                value={editForm.check_in_time}
                                onChange={(e) => setEditForm((p) => ({ ...p, check_in_time: e.target.value }))}
                                className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                              />
                            ) : (
                              toHHMM(row.check_in_time) || '-'
                            )}
                          </td>
                          <td className="px-3 py-3">
                            {isEditing ? (
                              <input
                                type="time"
                                value={editForm.check_out_time}
                                onChange={(e) => setEditForm((p) => ({ ...p, check_out_time: e.target.value }))}
                                className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                              />
                            ) : (
                              toHHMM(row.check_out_time) || '-'
                            )}
                          </td>
                          <td className="px-3 py-3">
                            {isEditing ? (
                              <select
                                value={editForm.status}
                                onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                                className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                              >
                                {statusOptions.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            ) : (
                              <span style={badgeStyle(row.status)}>{String(row.status || '').toUpperCase()}</span>
                            )}
                          </td>
                          <td className="px-3 py-3 font-semibold text-slate-900">{Number(row.ot_hours || 0).toFixed(2)}</td>
                          <td className="px-3 py-3">
                            {isEditing ? (
                              <button
                                type="button"
                                onClick={() => saveEdit(row.employee_id)}
                                className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white"
                              >
                                <Save size={13} /> Save
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openEdit(row)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"
                              >
                                <Edit3 size={13} /> Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!dailyLoading && !dailyRows.length && (
                      <tr>
                        <td colSpan={7} className="px-3 py-10 text-center text-slate-500">
                          <div className="inline-flex items-center gap-2"><Users size={16} /> No staff attendance rows found.</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                /* ── WORKER TABLE ── */
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-3">Worker Name</th>
                      <th className="px-3 py-3">Code</th>
                      <th className="px-3 py-3">Dept</th>
                      <th className="px-3 py-3">Daily Wage</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">Hours</th>
                      <th className="px-3 py-3">Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workerDailyLoading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cols={7} />)}
                    {!workerDailyLoading && workerDailyRows.map((row) => {
                      const isEditing = workerEditingId === row.worker_id;
                      return (
                        <tr key={row.worker_id} className="border-t border-slate-100">
                          <td className="px-3 py-3 font-medium text-slate-900">{row.worker_name}</td>
                          <td className="px-3 py-3 text-slate-500 text-xs">{row.worker_code || '-'}</td>
                          <td className="px-3 py-3 text-slate-700">{row.department || '-'}</td>
                          <td className="px-3 py-3 font-semibold text-slate-900">
                            {row.daily_wage ? `₹${Number(row.daily_wage).toLocaleString('en-IN')}/day` : '-'}
                          </td>
                          <td className="px-3 py-3">
                            {isEditing ? (
                              <select
                                value={workerEditForm.status}
                                onChange={(e) => setWorkerEditForm((p) => ({ ...p, status: e.target.value }))}
                                className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                              >
                                {workerStatusOptions.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            ) : (
                              <span style={badgeStyle(row.status)}>{String(row.status || '').toUpperCase()}</span>
                            )}
                          </td>
                          <td className="px-3 py-3 font-semibold text-slate-900">{Number(row.total_hours || 0).toFixed(1)}</td>
                          <td className="px-3 py-3">
                            {isEditing ? (
                              <button
                                type="button"
                                onClick={() => saveWorkerEdit(row.worker_id)}
                                className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white"
                              >
                                <Save size={13} /> Save
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openWorkerEdit(row)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"
                              >
                                <Edit3 size={13} /> Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!workerDailyLoading && !workerDailyRows.length && (
                      <tr>
                        <td colSpan={7} className="px-3 py-10 text-center text-slate-500">
                          <div className="inline-flex items-center gap-2"><Wrench size={16} /> No worker attendance rows found.</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      ) : (
        /* ═══════════════════════════════════════════ */
        /* MONTHLY SUMMARY VIEW                       */
        /* ═══════════════════════════════════════════ */
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {entityType === 'staff' ? 'Staff' : 'Worker'} Monthly Summary ({month})
              </h3>
              <p className="text-sm text-slate-500">Sorted by attendance percentage (lowest first).</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={exportSummaryCsv}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            {entityType === 'staff' ? (
              /* ── STAFF SUMMARY ── */
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-3">Employee</th>
                    <th className="px-3 py-3">Present</th>
                    <th className="px-3 py-3">Absent</th>
                    <th className="px-3 py-3">Late</th>
                    <th className="px-3 py-3">Half Day</th>
                    <th className="px-3 py-3">Attendance %</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryLoading && Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cols={6} />)}
                  {!summaryLoading && monthlyRows.map((row) => (
                    <tr key={row.employee_id} className="border-t border-slate-100">
                      <td className="px-3 py-3 font-medium text-slate-900">{row.name}</td>
                      <td className="px-3 py-3">{row.present}</td>
                      <td className="px-3 py-3">{row.absent}</td>
                      <td className="px-3 py-3">{row.late}</td>
                      <td className="px-3 py-3">{row.half_day}</td>
                      <td className="px-3 py-3 font-semibold text-slate-900">{Number(row.attendance_pct || 0).toFixed(2)}%</td>
                    </tr>
                  ))}
                  {!summaryLoading && !monthlyRows.length && (
                    <tr>
                      <td colSpan={6} className="px-3 py-10 text-center text-slate-500">No staff summary rows found for this month.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              /* ── WORKER SUMMARY ── */
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-3">Worker</th>
                    <th className="px-3 py-3">Dept</th>
                    <th className="px-3 py-3">Daily Wage</th>
                    <th className="px-3 py-3">Present</th>
                    <th className="px-3 py-3">Absent</th>
                    <th className="px-3 py-3">Half Day</th>
                    <th className="px-3 py-3">Attendance %</th>
                  </tr>
                </thead>
                <tbody>
                  {workerSummaryLoading && Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cols={7} />)}
                  {!workerSummaryLoading && workerMonthlyRows.map((row) => (
                    <tr key={row.worker_id} className="border-t border-slate-100">
                      <td className="px-3 py-3 font-medium text-slate-900">{row.name}</td>
                      <td className="px-3 py-3 text-slate-700">{row.department || '-'}</td>
                      <td className="px-3 py-3 font-semibold">₹{Number(row.daily_wage || 0).toLocaleString('en-IN')}/day</td>
                      <td className="px-3 py-3">{row.present}</td>
                      <td className="px-3 py-3">{row.absent}</td>
                      <td className="px-3 py-3">{row.half_day}</td>
                      <td className="px-3 py-3 font-semibold text-slate-900">{Number(row.attendance_pct || 0).toFixed(2)}%</td>
                    </tr>
                  ))}
                  {!workerSummaryLoading && !workerMonthlyRows.length && (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-slate-500">No worker summary rows found for this month.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AttendanceScreen;
